import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../config";
import UserMindMapKnowledge from "./UserMindMapKnowledge";
import PageHeader from './PageHeader';

export default function MyAccount({ userId, onClose, isDarkMode }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [editProfile, setEditProfile] = useState(false);
  const [editPassword, setEditPassword] = useState(false);
  const [form, setForm] = useState({});
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState(""); // "success" nebo "error"
  const [showKnowledge, setShowKnowledge] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/users/${userId}`)
      .then(res => res.json())
      .then(data => {
        setUser(data);
        setForm({ 
          name: data.name, 
          email: data.email || "", 
          vfLogin: data.vfLogin || "",
          phone: data.phone || "",
          office: data.office || ""
        });
      });
  }, [userId]);

  const handleSave = async () => {
    setMsg("");
    setMsgType("");
    try {
      const res = await fetch(`${API_URL}/users/${userId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setEditProfile(false);
        setMsg("✅ Údaje byly úspěšně uloženy!");
        setMsgType("success");
        const updated = await res.json();
        setUser(updated);
      } else {
        setMsg("❌ Chyba při ukládání údajů.");
        setMsgType("error");
      }
    } catch (error) {
      setMsg("❌ Chyba při komunikaci se serverem.");
      setMsgType("error");
    }
  };

  const handlePassword = async () => {
    setMsg("");
    setMsgType("");
    
    if (!password || password !== password2) {
      setMsg("❌ Hesla se neshodují nebo jsou prázdná.");
      setMsgType("error");
      return;
    }
    
    if (password.length < 4) {
      setMsg("❌ Heslo musí mít alespoň 4 znaky.");
      setMsgType("error");
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/users/${userId}/password`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      
      if (res.ok) {
        setPassword("");
        setPassword2("");
        setEditPassword(false);
        setMsg("🔒 Heslo bylo úspěšně změněno!");
        setMsgType("success");
      } else {
        const errorText = await res.text();
        setMsg(`❌ Chyba při změně hesla: ${errorText}`);
        setMsgType("error");
      }
    } catch (error) {
      setMsg(`❌ Chyba při změně hesla: ${error.message}`);
      setMsgType("error");
    }
  };

  if (!user) return (
    <div className={`${isDarkMode ? 'dark' : ''}`}>
      <div className={`min-h-screen ${isDarkMode ? 'bg-gradient-to-br from-gray-900 to-gray-800' : 'bg-gradient-to-br from-gray-100 to-blue-100'} py-10 flex items-center justify-center`}>
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    </div>
  );

  return (
    <div className={`${isDarkMode ? 'dark' : ''}`}>
      <div className={`min-h-screen ${isDarkMode ? 'bg-gradient-to-br from-gray-900 to-gray-800' : 'bg-gradient-to-br from-gray-100 to-blue-100'} py-10`}>
        <div className="w-[98vw] max-w-4xl mx-auto px-4">
        
        {/* Hlavička */}
        <PageHeader
          title="Můj účet"
        />

        {/* Zpráva */}
        {msg && (
          <div className={`mb-6 p-4 rounded-lg border ${
            msgType === "success" 
              ? "bg-green-50 border-green-200 text-green-800 dark:bg-green-900 dark:border-green-700 dark:text-green-200" 
              : "bg-red-50 border-red-200 text-red-800 dark:bg-red-900 dark:border-red-700 dark:text-red-200"
          }`}>
            {msg}
          </div>
        )}
        
        {/* Hlavní karta */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold">Můj účet</h1>
              </div>
            </div>
          </div>

          {/* Obsah */}
          <div className="p-6 space-y-8">
            {/* Profil sekce */}
            <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl p-6`}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Osobní údaje
                </h2>
                <button
                  onClick={() => editProfile ? setEditProfile(false) : setEditProfile(true)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    editProfile
                      ? "bg-gray-300 hover:bg-gray-400 text-gray-800 dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-white"
                      : "bg-blue-600 hover:bg-blue-700 text-white"
                  }`}
                >
                  {editProfile ? "Zrušit" : "Upravit"}
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Jméno</label>
                  {editProfile ? (
                    <input
                      className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none transition-colors"
                      value={form.name || ""}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="Zadejte své jméno"
                    />
                  ) : (
                    <div className="px-4 py-3 bg-white dark:bg-gray-600 border border-gray-200 dark:border-gray-500 rounded-lg text-gray-900 dark:text-white">
                      {user.name || <span className="text-gray-400 italic">neuvedeno</span>}
                    </div>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email</label>
                  {editProfile ? (
                    <input
                      className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none transition-colors"
                      type="email"
                      value={form.email || ""}
                      onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                      placeholder="Zadejte svůj email"
                    />
                  ) : (
                    <div className="px-4 py-3 bg-white dark:bg-gray-600 border border-gray-200 dark:border-gray-500 rounded-lg text-gray-900 dark:text-white">
                      {user.email || <span className="text-gray-400 italic">neuvedeno</span>}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">VF Login</label>
                  {editProfile ? (
                    <input
                      className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none transition-colors"
                      value={form.vfLogin || ""}
                      onChange={e => setForm(f => ({ ...f, vfLogin: e.target.value }))}
                      placeholder="VF Login (nepovinné)"
                    />
                  ) : (
                    <div className="px-4 py-3 bg-white dark:bg-gray-600 border border-gray-200 dark:border-gray-500 rounded-lg text-gray-900 dark:text-white">
                      <span className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                        {user.vfLogin || "-"}
                      </span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Telefon</label>
                  {editProfile ? (
                    <input
                      className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none transition-colors"
                      type="tel"
                      value={form.phone || ""}
                      onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                      placeholder="Zadejte telefon"
                    />
                  ) : (
                    <div className="px-4 py-3 bg-white dark:bg-gray-600 border border-gray-200 dark:border-gray-500 rounded-lg text-gray-900 dark:text-white">
                      {user.phone || <span className="text-gray-400 italic">neuvedeno</span>}
                    </div>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Kancelář</label>
                  {editProfile ? (
                    <input
                      className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none transition-colors"
                      value={form.office || ""}
                      onChange={e => setForm(f => ({ ...f, office: e.target.value }))}
                      placeholder="Zadejte kancelář/lokalitu"
                    />
                  ) : (
                    <div className="px-4 py-3 bg-white dark:bg-gray-600 border border-gray-200 dark:border-gray-500 rounded-lg text-gray-900 dark:text-white">
                      {user.office || <span className="text-gray-400 italic">neuvedeno</span>}
                    </div>
                  )}
                </div>
              </div>
              
              {editProfile && (
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={handleSave}
                    className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Uložit změny
                  </button>
                </div>
              )}
            </div>

            {/* Heslo sekce */}
            <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl p-6`}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Zabezpečení
                </h2>
                <button
                  onClick={() => editPassword ? setEditPassword(false) : setEditPassword(true)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    editPassword
                      ? "bg-gray-300 hover:bg-gray-400 text-gray-800 dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-white"
                      : "bg-orange-600 hover:bg-orange-700 text-white"
                  }`}
                >
                  {editPassword ? "Zrušit" : "Změnit heslo"}
                </button>
              </div>
              
              {editPassword && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Nové heslo</label>
                    <input
                      className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-orange-500 dark:focus:border-orange-400 focus:outline-none transition-colors"
                      type="password"
                      placeholder="Zadejte nové heslo"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Potvrdit heslo</label>
                    <input
                      className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-orange-500 dark:focus:border-orange-400 focus:outline-none transition-colors"
                      type="password"
                      placeholder="Zadejte heslo znovu"
                      value={password2}
                      onChange={e => setPassword2(e.target.value)}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <button
                      onClick={handlePassword}
                      className="px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      Změnit heslo
                    </button>
                  </div>
                </div>
              )}

              {!editPassword && (
                <div className="text-gray-600 dark:text-gray-400">
                  <p>Heslo je skryté z bezpečnostních důvodů. Klikněte na tlačítko pro změnu hesla.</p>
                </div>
              )}
            </div>

            {/* Znalosti sekce */}
            <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl p-6`}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  Moje znalosti
                </h2>
                <button
                  onClick={() => setShowKnowledge(true)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                >
                  🧠 Spravovat znalosti
                </button>
              </div>
              
              <div className="text-gray-600 dark:text-gray-400">
                <p>Zde můžete sledovat a spravovat své dovednosti a znalosti.</p>
                <p className="text-sm mt-2">Klikněte na tlačítko pro detailní správu vašich kompetencí.</p>
              </div>
            </div>

            {/* Info sekce */}
            <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl p-6`}>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-4">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Informace o účtu
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Uživatelské jméno:</span>
                  <div className="font-medium text-gray-900 dark:text-white">{user.username}</div>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Role:</span>
                  <div className="font-medium text-gray-900 dark:text-white">{user.role}</div>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">ID:</span>
                  <div className="font-mono text-xs text-gray-600 dark:text-gray-400">{user._id}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>

        {/* Knowledge Manager Modal */}
        {showKnowledge && (
          <UserMindMapKnowledge
            userId={userId}
            userName={user.name}
            isDarkMode={isDarkMode}
            isReadOnly={true}
            onClose={() => setShowKnowledge(false)}
          />
        )}
      </div>
    </div>
  );
}
